interface AnniversarySortable {
    /** May be absent entirely when an API response omits the column. */
    anniversary_years?: number | null
    name: string
    score: number
}

/**
 * The year an anniversary badge represents. `anniversary_years` wins when
 * present; falls back to the leading number in the name ("8. aastapäev" → 8),
 * then to the badge score.
 */
export const anniversaryYear = (badge: AnniversarySortable): number => {
    if (typeof badge.anniversary_years === 'number') return badge.anniversary_years
    const fromName = parseInt(badge.name, 10)
    return Number.isNaN(fromName) ? badge.score : fromName
}

/** Anniversary badges ordered by year ascending (1, 2, … 9, 10). Returns a new array. */
export const sortAnniversaryBadges = <T extends AnniversarySortable>(badges: T[]): T[] =>
    [...badges].sort((a, b) => anniversaryYear(a) - anniversaryYear(b))

export type RegularBadgeSort = 'name' | 'points-desc' | 'points-asc'

interface RegularSortable {
    name: string
    score: number
}

/** Order non-anniversary badges by name (A→Z) or by points (high→low / low→high). Stable-ish:
 *  equal keys keep their incoming order. Returns a new array. */
export const sortRegularBadges = <T extends RegularSortable>(
    badges: T[],
    sort: RegularBadgeSort,
): T[] => {
    if (sort === 'name') {
        return [...badges].sort((a, b) => a.name.localeCompare(b.name, 'et'))
    }
    const dir = sort === 'points-desc' ? -1 : 1
    return [...badges].sort((a, b) => (a.score - b.score) * dir)
}
